import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Container
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Lock,
  Person,
  Security,
  CardMembership,
  WebAsset
} from '@mui/icons-material'
import { certificateApi } from '../utils/api'
import api from '../utils/api'

const Login = () => {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [certCount, setCertCount] = useState(null)
  const [productsCount, setProductsCount] = useState(null)
  const [loadingCount, setLoadingCount] = useState(true)

  // Fetch certificate and product counts on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/auth/certificate-stats')
        if (response.data) {
          if (response.data.total_certificates !== undefined) {
            setCertCount(response.data.total_certificates)
          }
          if (response.data.total_products !== undefined) {
            setProductsCount(response.data.total_products)
          }
        }
      } catch (err) {
        console.log('Could not fetch stats:', err)
        setCertCount('N/A')
        setProductsCount('N/A')
      } finally {
        setLoadingCount(false)
      }
    }

    fetchStats()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Call your LDAP/Windows authentication API endpoint
      const response = await api.post('/auth/login', {
        username,
        password
      })

      // Tokens are now in httpOnly cookies (set by backend)
      // Just store username for display purposes
      if (response.data.message) {
        localStorage.setItem('username', username)
        localStorage.setItem('mail', response.data.mail)
        
        // Small delay to ensure cookies are fully set before navigation
        // This prevents race condition where components try to fetch data
        // before browser has processed Set-Cookie headers
        setTimeout(() => {
          navigate('/')
        }, 100)
      } else {
        setError('Authentication failed. Please try again.')
      }
    } catch (err) {
      console.error('Login error:', err)
      if (err.response?.status === 401) {
        setError('Invalid username or password.')
      } else if (err.response?.status === 500) {
        setError('Server error. Please contact support.')
      } else {
        setError('Unable to connect to authentication server.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: '#f5f7fa'
      }}
    >
      {/* Left Side - Hero Section */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #004681 0%, #2f79bf 100%)',
          padding: 8,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            top: -100,
            right: -100
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            bottom: -50,
            left: -50
          }}
        />

        {/* Content */}
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: '600px', textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { md: '5rem', lg: '6.5rem' },
              mb: 3,
              lineHeight: 1,
              letterSpacing: '-0.04em',
              fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.85) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 2px 20px rgba(255, 255, 255, 0.3)'
            }}
          >
            Cert Manage
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontSize: '1.5rem',
              mb: 4,
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            Secure, automated certificate lifecycle management for enterprise infrastructure
          </Typography>

          {/* Stats Display */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              gap: 3,
              mt: 2
            }}
          >
            {/* Certificates Managed */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <CardMembership sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.4)', mb: 1.5 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 0.5, fontSize: '0.9rem' }}>
                Certificates Managed
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'white' }}>
                {loadingCount ? (
                  <CircularProgress size={32} sx={{ color: 'white' }} />
                ) : (
                  certCount?.toLocaleString() || '0'
                )}
              </Typography>
            </Box>

            {/* Products Onboarded */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <WebAsset sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.4)', mb: 1.5 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 0.5, fontSize: '0.9rem' }}>
                Products Onboarded
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'white' }}>
                {loadingCount ? (
                  <CircularProgress size={32} sx={{ color: 'white' }} />
                ) : (
                  productsCount?.toLocaleString() || '0'
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right Side - Login Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '500px', lg: '600px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: 3, sm: 6, md: 8 },
          background: 'white'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '400px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'center' }}>
            <Security sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2f2f2f' }}>
                Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use your Windows credentials
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              sx={{ mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 4 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={loading}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.8,
                background: 'linear-gradient(135deg, #004681 0%, #2f79bf 100%)',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(0, 70, 129, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #003a6b 0%, #2667a8 100%)',
                  boxShadow: '0 6px 20px rgba(0, 70, 129, 0.6)',
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Having trouble signing in? Contact IT Support
            </Typography>
          </Box>

          {/* Broadridge Logo */}
          <Box sx={{ mt: 6, mb: 3, textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src="/images/broadridge-logo.png"
              alt="Broadridge Financial Solutions"
              sx={{
                height: 40,
                maxWidth: '200px',
                objectFit: 'contain',
                opacity: 0.8
              }}
              onError={(e) => {
                console.error('Logo failed to load')
                e.target.style.display = 'none'
              }}
            />
          </Box>

          {/* Copyright */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
              © {new Date().getFullYear()} Broadridge Financial Solutions, Inc.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Login
